import type { Diagnostic } from './pif-partial-validation';
import {
  coerceParameterInputForCompiler,
  isEmptyValue,
  isPayloadFieldDroppedForNodeType,
  toCompilerNodeNumber,
} from './prepare-rows-data-for-pif-compiler';

/**
 * Why the last validation pass could not produce a verdict worth trusting.
 *
 * PSTUDIO-4120. The store used to have exactly two states — "the lists hold the
 * latest verdict" and "the lists hold the latest verdict" — because a pass that
 * never reached `setErrors`/`setPifErrors` simply left the previous lists
 * standing. This type is the third state: the lists are still there, but they
 * are NOT a fresh verdict, and every consumer (the badge, the publish gate) has
 * to be able to tell.
 *
 * `null` is the trustworthy state. It is also the initial one, deliberately: a
 * solution that has not been validated yet keeps behaving exactly as it did
 * before this ticket, so the change can only ever ADD a refusal.
 */
export type ValidationFailureReason =
  /** `validate()` itself threw — the pass never reached the error lists. */
  | 'threw'
  /**
   * The compiler answered with its own crash marker instead of a verdict: one
   * opaque message that REPLACED every real finding it had already made.
   *
   * This is the COLD shape only — the one a compiler with no accumulated
   * solution summary produces. See `hasDegradedDiagnostic` for the warm shape,
   * which this reason cannot represent because nothing in the answer says it
   * happened.
   */
  | 'compiler-degraded'
  /**
   * A node in the payload cannot be parsed by the PIF grammar at all, so the
   * pass would die inside the compiler. The pass is skipped rather than run.
   */
  | 'payload-not-compilable'
  /**
   * PSTUDIO-4242: the Bot Compiler request failed or timed out, so it produced
   * no answer at all — which `validateCsv` returns as an EMPTY finding list,
   * the same shape as a clean pass.
   */
  | 'validator-unreachable';

/**
 * The exact message agent-compiler substitutes for the whole diagnostic list
 * when its own `try/catch` fires (`public-api/agent-compiler.ts`). Matched in
 * full, never as a substring: a solution-level diagnostic that merely mentions
 * "validation" must not be mistaken for a crashed pass.
 */
export const COMPILER_UNHANDLED_EXCEPTION_MESSAGE =
  'Unhandled exception occurred during validation';

/**
 * The prefix Langium's `wrapValidationException` gives a check that threw: the
 * exception is converted into an error diagnostic reading
 * `An error occurred during validation: <the exception's own message>`
 * (`langium/validation/validation-registry.js`). Such a diagnostic carries no
 * `data.id` / `data.nodeNumber`, so it can address no cell — it is a crash
 * report wearing a finding's clothes.
 */
export const LANGIUM_CHECK_EXCEPTION_PREFIX =
  'An error occurred during validation:';

/**
 * Does this diagnostic report the VALIDATOR failing rather than the solution?
 *
 * LIMITATION, and it decides how much this file is worth: this recognises the
 * crash only when the compiler had NO accumulated solution summary. Measured on
 * agent-compiler 1.11.0 by running both: with a summary already built, the same
 * crash makes it answer FROM that summary — no marker, no opaque message, no
 * Langium prefix, nothing these two constants can match. The answer is not a
 * verbatim copy of the previous one (a control run showed the crashed pass
 * returning 1 diagnostic where the clean pass returned 2), and that is worse,
 * not better: it looks like a plausible fresh verdict rather than an obvious
 * repeat.
 *
 * So this is the second line of defence, not the first. The first is not
 * detecting the crash but never causing it: `findUncompilableNodeNumberRow`
 * and `findUncompilableParameterInputRow` (PSTUDIO-4417) below, run against
 * the exact array the payload is built from. Widen that guard when a new way to
 * kill the pass turns up; do not expect this predicate to catch it.
 */
export const isDegradedDiagnostic = (diagnostic: Diagnostic): boolean => {
  const message = diagnostic?.message ?? '';
  return (
    message === COMPILER_UNHANDLED_EXCEPTION_MESSAGE ||
    message.startsWith(LANGIUM_CHECK_EXCEPTION_PREFIX)
  );
};

export const hasDegradedDiagnostic = (
  diagnostics: readonly Diagnostic[] | undefined
): boolean => (diagnostics ?? []).some(isDegradedDiagnostic);

/**
 * Can the PIF grammar read the value this row's `Node #` cell will become in
 * the compiler payload?
 *
 * The grammar's `NodeNumber` fragment is `NODE_NUMBER_STRING COLON
 * nodeNumber=INTEGERS`, and `INTEGERS` is `/(?<!")[-]?\d+(?![a-zA-Z_-])/` — a
 * BARE integer. Anything the PAYLOAD carries that is not one of those (a
 * quoted `"abc"`, a fraction, an exponent-formatted big number, or the `null`
 * the builder writes for a cleared cell) is a parse error, and an
 * error-recovered `Node` still reaches the compiler's summary builder with
 * `nodeNumber` undefined, where an unguarded `.trim()` kills the whole pass.
 * Verified against agent-compiler 1.11.0: a missing key, a `null` and an `""`
 * behave identically there, and the failure is NOT reported as a finding.
 *
 * Read the two `null`s carefully — they are different values at different
 * stages, and conflating them is what made this docblock contradict its own
 * last paragraph. A `null` sitting in the ROW (a group header, or a blank that
 * round-tripped through the database) becomes `0` through
 * `toCompilerNodeNumber` and IS parseable. The `null` that is not is the one
 * the builder writes for an `undefined` cell, and it never reaches this
 * predicate as a row value — the predicate refuses that cell by its `undefined`.
 *
 * `Number.isSafeInteger`, not `Number.isInteger`, and the reason is NOT the
 * `1e21` serialization edge alone. Past `2^53` adjacent integers stop being
 * distinguishable (the same argument `node-number-identity.ts` already makes for
 * node identity), so this refuses `[2^53, 1e21)` too — values the grammar would
 * in fact accept. That is deliberate over-refusal in the fail-CLOSED direction:
 * such a payload skips the pass rather than publishing on a number the app
 * cannot tell apart from its neighbour.
 *
 * `''` and `null` in a row map to `0` and so ARE compilable — deliberately.
 * This predicate answers "will the pass survive", not "is this a usable node
 * number"; the second question is `findNodeNumberProblem`'s, and it already
 * blocks publishing on a 0.
 */
export const isCompilableNodeNumber = (value: unknown): boolean =>
  Number.isSafeInteger(toCompilerNodeNumber(value));

/**
 * The first row whose node number would kill the compiler pass, or `null` when
 * the payload is safe to send. Returns the row so the caller can name it, and
 * `id` is REQUIRED in the constraint for that reason. An optional `id?` would
 * not have done it: with every member optional the constraint accepts a type
 * that has no `id` at all (checked with tsc), so the guarantee has to be a
 * required field or it is not a guarantee.
 */
export const findUncompilableNodeNumberRow = <
  T extends { id: string; nodeNumber?: unknown },
>(
  rows: readonly T[]
): T | null =>
  rows.find((row) => !isCompilableNodeNumber(row.nodeNumber)) ?? null;

/**
 * Can the PIF grammar read the `parameterInput` this row will carry in the
 * compiler payload? PSTUDIO-4417.
 *
 * The grammar is `parameterInput=(GenericJsonObject | EmptyField)`
 * (`node.langium`), so anything the payload carries that is neither — plain
 * text, a JSON string, number, boolean or non-empty array — is a parse error,
 * and the error-recovered node reaches the summary builder with no value,
 * where `genericJsonObjectToRecord` reads `$cstNode` off `undefined`. Measured
 * on agent-compiler 1.11.0, a cold pass answers with
 * `Unhandled exception occurred during validation` alone, and a warm pass
 * answers from its accumulated summary with no marker: the solution-level
 * findings silently drop out. Plain text reaches this field since PSTUDIO-4417
 * stopped wrapping it in `{"data": …}`.
 *
 * The answer is taken through the payload's own steps, not a copy of them: the
 * field is dropped for a Decision node (`isPayloadFieldDroppedForNodeType`),
 * the stored value is coerced as the payload coerces it
 * (`coerceParameterInputForCompiler`), and an empty result is dropped
 * (`isEmptyValue`). What is left must be a plain object.
 */
export const isCompilableParameterInput = (
  parameterInput: unknown,
  nodeType: unknown
): boolean => {
  if (isPayloadFieldDroppedForNodeType('parameterInput', nodeType)) return true;
  const sent = coerceParameterInputForCompiler(parameterInput);
  if (isEmptyValue(sent)) return true;
  return typeof sent === 'object' && sent !== null && !Array.isArray(sent);
};

/**
 * The first row whose `parameterInput` would kill the compiler pass, or `null`.
 * The twin of `findUncompilableNodeNumberRow`, and run against the same array.
 */
export const findUncompilableParameterInputRow = <
  T extends { id: string; nodeType?: unknown; parameterInput?: unknown },
>(
  rows: readonly T[]
): T | null =>
  rows.find(
    (row) => !isCompilableParameterInput(row.parameterInput, row.nodeType)
  ) ?? null;
